'use client'
import "@/components/ReviewFilter/ReviewFilter.css";
import { React, useState, useEffect } from "react";

/**
 * @description The ReviewFilter component is responsible for filtering reviews based on various criteria such as rating, search term, and order. 
 * @returns {JSX.Element} The rendered ReviewFilter component. It allows users to turn on filters.
 */
export default function ReviewFilter({globalFilterCriteria, setGlobalFilterCriteria}) {
  // State to hold the filter criteria.
  const [filterCriteria, setFilterCriteria] = useState({
    orderCriteria: "updateTime", // The criteria to order by, e.g., "rating" or "updateTime", defaults to "updateTime".
    orderDirection: "desc", // The direction to order by, either "asc" for ascending or "desc" for descending, defaults to "desc".
    searchTerm: "", // String to search for in review text, not for backend, only filters what the frontend shows.
  });

  // Toggle the filter criteria
  const [filterOpen, setFilterOpen] = useState(false);

  // toggleFilter: toggles the modal open/closed
  function toggleFilter() {
    setFilterOpen(v => !v);
  }

  // closeFilter: closes the modal (used for overlay and close button)
  function closeFilter() {
    setFilterOpen(false);
  }

  // handleChange: updates a single criteria field from form inputs
  function handleChange(e) {
    const { name, value } = e.target;
    setFilterCriteria(c => ({ ...c, [name]: value }));
  }

  // applyFilters: calls onApply (if provided) then closes modal
  function applyFilters(e) {
    if (e && e.preventDefault) e.preventDefault();
    
    setGlobalFilterCriteria(filterCriteria);
    closeFilter();
  }

  // Resets the filters to default values
  function reset() {
    setFilterCriteria({
      orderCriteria: "updateTime",
      orderDirection: "desc",
      searchTerm: "",
    });
  }

  // updateTime can only be descending because that's all Google supports, so if user tries to set it to something else, force it back to descending and disable the option to change it
  useEffect(() => {
    if (filterCriteria.orderCriteria === "updateTime" && filterCriteria.orderDirection !== "desc") {
      setFilterCriteria(c => ({ ...c, orderDirection: "desc" }));
    }
  }, [filterCriteria]);

  return (
    <>
      <div
        className="reviewFilterContainer"
      >
        <button
          className="filter-toggle-btn"
          onClick={toggleFilter}
        >
          ☰ sort & filter
        </button>
      </div>

      {filterOpen && (
        <div className="filter-overlay" onClick={closeFilter}>
          <div
            id="review-filter-modal"
            className="filter-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="filter-header">
              <h3>Filter & Sort</h3>
              <button className="filter-close" onClick={closeFilter} aria-label="Close">✕</button>
            </div>

            <form
              className="filter-form"
              onSubmit={(e) => {
                e.preventDefault();
                applyFilters();
              }}
            >
              <label className="form-row">
                <span>Search</span>
                <input
                  name="searchTerm"
                  value={filterCriteria.searchTerm}
                  onChange={handleChange}
                  placeholder="Search text within reviews..."
                />
              </label>

              <label className="form-row">
                <span>Order Criteria</span>
                <select name="orderCriteria" value={filterCriteria.orderCriteria} onChange={handleChange}>
                  <option value="rating">Rating</option>
                  <option value="updateTime">Update Time</option>
                </select>
              </label>

              <label className="form-row">
                <span>Order Direction</span>
                <select
                  name="orderDirection"
                  value={filterCriteria.orderDirection}
                  onChange={handleChange}
                  disabled={filterCriteria.orderCriteria === "updateTime"} // Disable direction selection if ordering by updateTime since Google only supports descending for that
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </label>

              <div className="filter-actions">
                <button type="button" className="btn-secondary" onClick={() => reset()}>Reset</button>
                <button type="submit" className="btn-primary" onClick={() => applyFilters()}>Apply</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

